/**
 * Générateur — Construire un tableau simple pour présenter des données
 * Attendu ID : 677 | Niveau : 6ème | Domaine : Données et probabilités
 *
 * Types générés : qcm_unique, texte, relier, tableau_vf, texte_trou_drag, texte_trou_saisie
 * Template : construction_tableau_effectifs
 *
 * Concept clé : Compter les occurrences de chaque modalité dans une série brute
 * de données, puis construire / lire un tableau d'effectifs.
 *
 * Variantes estimées : 200+ (objectif : 20/20 uniques sur 20 itérations auditGen)
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 677;
const GEN_KEY = 'construction_tableau_effectifs';

const CONTEXTS = [
    {
        id: 'notes',
        modalities: ['A', 'B', 'C', 'D', 'E'],
        intro: 'M. Dupont a noté les résultats de ses élèves avec des lettres',
        label: 'lettre', genre: 'f',
        labelPlural: 'lettres',
    },
    {
        id: 'couleurs',
        modalities: ['Rouge', 'Bleu', 'Vert', 'Jaune', 'Orange'],
        intro: 'On a demandé aux élèves leur couleur préférée',
        label: 'couleur', genre: 'f',
        labelPlural: 'couleurs',
    },
    {
        id: 'sports',
        modalities: ['Football', 'Basketball', 'Tennis', 'Natation', 'Gymnastique'],
        intro: 'On a interrogé les élèves sur leur sport préféré',
        label: 'sport', genre: 'm',
        labelPlural: 'sports',
    },
    {
        id: 'transports',
        modalities: ['Vélo', 'Bus', 'Voiture', 'Marche', 'Trottinette'],
        intro: 'On a relevé le moyen de transport des élèves',
        label: 'moyen de transport', genre: 'm',
        labelPlural: 'moyens de transport',
    },
    {
        id: 'animaux',
        modalities: ['Chien', 'Chat', 'Hamster', 'Poisson', 'Lapin'],
        intro: 'On a demandé aux élèves leur animal préféré',
        label: 'animal', genre: 'm',
        labelPlural: 'animaux',
    },
    {
        id: 'des',
        modalities: ['1', '2', '3', '4', '5', '6'],
        intro: 'On a lancé un dé 20 fois et noté les résultats',
        label: 'face', genre: 'f',
        labelPlural: 'faces',
    },
    {
        id: 'fruits',
        modalities: ['Pomme', 'Banane', 'Orange', 'Fraise', 'Kiwi'],
        intro: 'On a demandé aux élèves leur fruit préféré',
        label: 'fruit', genre: 'm',
        labelPlural: 'fruits',
    },
];

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || 'Construire un tableau simple pour présenter des données',
});

// ─── Génération de série ────────────────────────────────────

/**
 * Génère une série de données brutes à partir d'un contexte.
 * @param {Object} ctx - Le contexte choisi
 * @param {number} [nbMod] - Nombre de modalités à garder (4-6)
 * @returns {{ mods: string[], counts: number[], total: number, series: string[], seriesText: string }}
 */
const generateSeries = (ctx, nbMod) => {
    const n = nbMod || randInt(4, Math.min(6, ctx.modalities.length));
    const mods = shuffle(ctx.modalities).slice(0, n);
    const total = ctx.id === 'des' ? 20 : randInt(18, 25);

    // Assign base counts
    const counts = mods.map(() => randInt(2, 5));
    let currentSum = counts.reduce((a, b) => a + b, 0);

    // Distribute remaining
    while (currentSum < total) {
        const idx = randInt(0, n - 1);
        if (counts[idx] < 7) {
            counts[idx]++;
            currentSum++;
        }
    }
    // If we overshoot, reduce
    while (currentSum > total) {
        const idx = randInt(0, n - 1);
        if (counts[idx] > 2) {
            counts[idx]--;
            currentSum--;
        }
    }

    // Build raw series
    const series = [];
    mods.forEach((mod, i) => {
        for (let j = 0; j < counts[i]; j++) series.push(mod);
    });
    const shuffled = shuffle(series);
    const seriesText = shuffled.join(', ');

    return { mods, counts, total, series: shuffled, seriesText };
};

/**
 * Trouve la modalité la plus fréquente.
 */
const getMode = (mods, counts) => {
    let maxCount = 0;
    let mode = mods[0];
    counts.forEach((c, i) => {
        if (c > maxCount) { maxCount = c; mode = mods[i]; }
    });
    return mode;
};

const getCountOf = (mods, counts, mod) => counts[mods.indexOf(mod)];

// ─── Helpers d'énoncé ───────────────────────────────────────

// <br> final (revue 2026-08-08, demande utilisateur) : aère l'espace entre la
// série et la consigne qui suit — commun aux 6 formats.
const seriesEnonce = (ctx, seriesText) =>
    `<p>${ctx.intro}. Voici les résultats :</p><p><b>${seriesText}</b></p><br>`;

// ─── Générateurs ────────────────────────────────────────────

// ─── QCM UNIQUE ─────────────────────────────────────────────

/** Sub-variant 1: Combien de fois apparaît [modalité] ? */
// Réécrit (revue 2026-08-08) : l'ancienne version générait DEUX séries (la
// première, distracteurs compris, était du code mort) et servait presque
// toujours {c−1, c, c+1, X} — bonne réponse au 2ᵉ rang croissant dans 75,4 %
// des tirages, jamais extrême. Calibrage à branches : min / max / mixte.
const genQcmCount = () => {
    const ctx = randomChoice(CONTEXTS);
    const fresh = generateSeries(ctx);
    const tIdx = randInt(0, fresh.mods.length - 1);
    const tgt = fresh.mods[tIdx];
    const corr = fresh.counts[tIdx];

    // Branche max seulement si c ≥ 4 (les trois distracteurs en dessous
    // doivent rester ≥ 1)
    const branche = corr >= 4
        ? randomChoice(['min', 'max', 'mixte'])
        : randomChoice(['min', 'mixte']);
    let candidats;
    if (branche === 'min') {
        candidats = [corr + 1, corr + 2, fresh.total];
    } else if (branche === 'max') {
        candidats = [corr - 1, corr - 2, corr - 3];
    } else {
        const adjI = (tIdx + 1) % fresh.mods.length;
        candidats = [Math.max(1, corr - 1), corr + 1, fresh.counts[adjI]];
    }

    const vus = new Set([String(corr)]);
    const distArr = [];
    const pousser = (v) => {
        const s = String(v);
        if (v > 0 && !vus.has(s)) {
            vus.add(s);
            distArr.push(s);
        }
    };
    candidats.forEach(pousser);
    // Compléments en cas de collision (voisin égal à c ± 1, etc.)
    for (const v of [fresh.total, corr + 2, corr + 3, corr - 2]) {
        if (distArr.length >= 3) break;
        pousser(v);
    }

    const allChoix = [String(corr), ...distArr.slice(0, 3)];

    const texte = seriesEnonce(ctx, fresh.seriesText)
        + `<p>Combien de fois apparaît <b>${tgt}</b> dans cette série ?</p>`;

    const payload = makeQcm(allChoix, String(corr), texte, opts());
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

/** Sub-variant 2: Quelle modalité apparaît le plus souvent ? */
const genQcmMode = () => {
    const ctx = randomChoice(CONTEXTS);
    let fresh = generateSeries(ctx);
    // Mode strictement unique par RE-TIRAGE (revue 2026-08-08) : le bris
    // d'égalité par incrément portait la série à 21 valeurs sous l'intro
    // « lancé un dé 20 fois » (107 tirages/6000) — recette du V/F (28/07).
    for (let t = 0; t < 40; t++) {
        const maxC = Math.max(...fresh.counts);
        if (fresh.counts.filter(c => c === maxC).length === 1) break;
        fresh = generateSeries(ctx);
    }
    if (fresh.counts.filter(c => c === Math.max(...fresh.counts)).length > 1) return genQcmMode();
    const mode = getMode(fresh.mods, fresh.counts);

    const others = fresh.mods.filter(m => m !== mode);
    const distractors = shuffle(others).slice(0, 3);
    const allChoix = [mode, ...distractors];

    const texte = seriesEnonce(ctx, fresh.seriesText)
        + `<p>Quelle ${ctx.label} apparaît le <b>plus souvent</b> dans cette série ?</p>`;

    const payload = makeQcm(allChoix, mode, texte, opts());
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

/** Sub-variant 3: Quel est le nombre total d'observations ? */
const genQcmTotal = () => {
    // Contexte dés exclu (revue 2026-08-08) : son intro annonce « lancé un dé
    // 20 fois » — la réponse était écrite dans l'énoncé (275 tirages/6000).
    const ctx = randomChoice(CONTEXTS.filter(c => c.id !== 'des'));
    const fresh = generateSeries(ctx);

    // Calibrage anti-stratégies aveugles (revue 2026-08-08) : les décalages
    // codés en dur {+1, −1, +2} plaçaient la bonne réponse en 2ᵉ position
    // croissante dans 100 % des tirages. Trois branches équiprobables :
    // bonne = min / bonne = max / bonne au milieu.
    const correct = String(fresh.total);
    const offsets = randomChoice([[1, 2, 3], [-3, -2, -1], [-1, 1, 2]]);
    const allChoix = [correct, ...offsets.map(o => String(fresh.total + o))];

    const texte = seriesEnonce(ctx, fresh.seriesText)
        + `<p>Quel est le <b>nombre total d'observations</b> dans cette série ?</p>`;

    const payload = makeQcm(allChoix, correct, texte, opts());
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

// ─── TEXTE (SAISIE) ────────────────────────────────────────

const genSaisie = () => {
    const ctx = randomChoice(CONTEXTS);
    const fresh = generateSeries(ctx);
    const targetIdx = randInt(0, fresh.mods.length - 1);
    const target = fresh.mods[targetIdx];
    const correct = fresh.counts[targetIdx];

    const texte = seriesEnonce(ctx, fresh.seriesText)
        + `<p>Combien de fois apparaît <b>${target}</b> dans la série ?</p>`;

    // Pas de strict (revue 2026-08-08) : on teste un comptage, la comparaison
    // par valeur suffit (5 = 5,0 accepté).
    const payload = makeSaisie(String(correct), texte, opts());
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

// ─── RELIER ─────────────────────────────────────────────────

const genRelier = () => {
    const ctx = randomChoice(CONTEXTS);
    // Série avec ≥ 3 effectifs distincts, sinon la dédup des cibles ci-dessous
    // peut tomber sous 2 paires (relier injouable)
    let fresh = generateSeries(ctx, randInt(4, 5));
    for (let essais = 0; essais < 30 && new Set(fresh.counts.map(String)).size < 3; essais++) {
        fresh = generateSeries(ctx, randInt(4, 5));
    }

    // 4 modalités à EFFECTIFS distincts : deux modalités de même effectif
    // donneraient deux cibles identiques à relier (appariement indécidable)
    const indices = [];
    const effectifsVus = new Set();
    for (const i of shuffle([...Array(fresh.mods.length).keys()])) {
        const effectif = String(fresh.counts[i]);
        if (!effectifsVus.has(effectif)) {
            effectifsVus.add(effectif);
            indices.push(i);
        }
        if (indices.length === 4) break;
    }
    const pairs = indices.map(i => ({
        source: fresh.mods[i],
        target: String(fresh.counts[i]),
    }));

    // Cibles leurres (revue 2026-08-08, recette du relier gen 676) : en n ↔ n
    // fermé, la dernière paire était offerte — l'élève ne comptait que n − 1
    // modalités. Leurres : l'effectif d'une modalité non retenue, puis une
    // valeur proche, tous dédupliqués des cibles réelles.
    const prises = new Set(pairs.map(p => p.target));
    const leurres = [];
    const pushLeurre = (v, code) => {
        const s = String(v);
        if (v > 0 && !prises.has(s) && leurres.length < 2) {
            prises.add(s);
            leurres.push({ target: s, code });
        }
    };
    fresh.mods.forEach((m, i) => {
        if (!indices.includes(i)) pushLeurre(fresh.counts[i], 'modalite_non_retenue');
    });
    const base = fresh.counts[indices[0]];
    [base + 1, base - 1, base + 2].forEach(v => pushLeurre(v, 'valeur_proche'));

    const avertissement = leurres.length
        ? ' Attention : certaines valeurs proposées ne correspondent à aucune réponse.'
        : '';
    const texte = seriesEnonce(ctx, fresh.seriesText)
        + `<p>Relie chaque ${ctx.label} à son effectif.${avertissement}</p>`;

    const payload = makeRelier(pairs, texte, { ...opts(), cibles_leurres: leurres });
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

// ─── TABLEAU V/F ────────────────────────────────────────────

// Article défini avec élision devant voyelle : « L'animal » et non « Le animal ».
// Élision sur VOYELLE stricte (retouche 2026-07-28) : h et y retirés de la
// classe — « le haricot » (h aspiré) et « le yaourt » ne s'élident pas ; le
// pool actuel n'a que « animal » qui élide, la liste reste sûre si on l'étend.
const artDef = (genre, label) =>
    (/^[aeiouàâéèêîïôû]/i.test(label) ? "L'" : (genre === 'm' ? 'Le ' : 'La '));

const genVF = () => {
    // Imposer un mode STRICTEMENT unique (même logique que genQcmMode) : en cas
    // d'ex æquo au sommet, l'affirmation FAUSSE « le plus fréquent est
    // {wrongMode} » pourrait tomber sur un CO-MAXIMUM, pourtant tout aussi
    // « le plus fréquent » → correction contradictoire.
    // ⚠️ Retouche 2026-07-28 : on RE-TIRE la série au lieu d'incrémenter le
    // mode. L'incrément changeait le total, or le contexte « dés » annonce
    // « lancé un dé 20 fois » EN DUR : l'affirmation vraie « total = 21 »
    // devenait contradictoire avec l'énoncé (~5,8 % des tirages).
    let ctx = randomChoice(CONTEXTS);
    let fresh = generateSeries(ctx);
    for (let t = 0; t < 40; t++) {
        const max0 = Math.max(...fresh.counts);
        if (fresh.counts.filter(c => c === max0).length === 1) break;
        fresh = generateSeries(ctx);
    }
    // Repli déterministe (jamais atteint en pratique) : bris d'égalité par
    // incrément, réservé aux contextes SANS total annoncé dans l'intro.
    if (fresh.counts.filter(c => c === Math.max(...fresh.counts)).length > 1) {
        if (ctx.id === 'des') {
            ctx = CONTEXTS.find(c => c.id !== 'des') || ctx;
            fresh = generateSeries(ctx);
        }
        const cts = fresh.counts;
        const modeIdx = cts.indexOf(Math.max(...cts));
        cts[modeIdx]++;
        fresh.total++;
        const series = [];
        fresh.mods.forEach((m, i) => { for (let j = 0; j < cts[i]; j++) series.push(m); });
        fresh.seriesText = shuffle(series).join(', ');
    }
    const { mods, counts } = fresh;
    const mode = getMode(mods, counts);
    const maxCount = counts[mods.indexOf(mode)];
    const total = fresh.total;

    // Clés sémantiques (revue 2026-08-08, recette gens 675/676) : apparition
    // de X / mode / total / paire comparée. Sélection à clés toutes distinctes
    // — avant, 75,5 % des tirages avaient deux affirmations sur le même objet
    // (les deux totaux, les deux modes, X apparaît 5 fois ET 4 fois…).
    // Pool of VRAI statements
    const vraiPool = [];
    // Trois apparitions correctes sur des modalités distinctes
    const vIdx1 = randInt(0, mods.length - 1);
    [vIdx1, (vIdx1 + 1) % mods.length, (vIdx1 + 2) % mods.length].forEach(i => {
        vraiPool.push({ l: `${mods[i]} apparaît ${counts[i]} fois.`, k: `app:${mods[i]}` });
    });
    // "La modalité la plus fréquente est [correct]."
    vraiPool.push({ l: `${artDef(ctx.genre, ctx.label)}${ctx.label} ${ctx.genre === 'm' ? 'le plus fréquent' : 'la plus fréquente'} est ${mode}.`, k: 'mode' });
    // "Le total est [N]."
    vraiPool.push({ l: `Le nombre total d'observations est ${total}.`, k: 'total' });

    // Pool of FAUX statements
    const fauxPool = [];
    // Deux apparitions faussées sur des modalités distinctes
    const fIdx1 = randInt(0, mods.length - 1);
    [fIdx1, (fIdx1 + 1) % mods.length].forEach(i => {
        const wrongCount = counts[i] + randomChoice([-1, 1]);
        if (wrongCount > 0) {
            fauxPool.push({ l: `${mods[i]} apparaît ${wrongCount} fois.`, k: `app:${mods[i]}` });
        }
    });
    // "La modalité la plus fréquente est [wrong]." Défense en profondeur : wrongMode
    // uniquement parmi les modalités d'effectif STRICTEMENT inférieur au maximum, pour
    // qu'elle soit réellement fausse même s'il subsistait un co-maximum.
    const wrongCandidats = mods.filter((m, i) => counts[i] < maxCount);
    const wrongMode = wrongCandidats.length
        ? randomChoice(wrongCandidats)
        : randomChoice(mods.filter(m => m !== mode));
    fauxPool.push({ l: `${artDef(ctx.genre, ctx.label)}${ctx.label} ${ctx.genre === 'm' ? 'le plus fréquent' : 'la plus fréquente'} est ${wrongMode}.`, k: 'mode' });
    // "[Mod1] et [Mod2] ont le même effectif [when false]."
    let pairFound = false;
    for (let i = 0; i < mods.length && !pairFound; i++) {
        for (let j = i + 1; j < mods.length && !pairFound; j++) {
            if (counts[i] !== counts[j]) {
                fauxPool.push({ l: `${mods[i]} et ${mods[j]} ont le même effectif.`, k: `pair:${[mods[i], mods[j]].sort().join('|')}` });
                pairFound = true;
            }
        }
    }
    // Wrong total
    fauxPool.push({ l: `Le nombre total d'observations est ${total + 1}.`, k: 'total' });

    // Pick 2-3 vrais, rest faux, total 5, clés toutes distinctes
    const nbVrais = randInt(2, 3);
    const nbFaux = 5 - nbVrais;

    const seenKeys = new Set();
    const pick = (pool, n) => {
        const out = [];
        for (const e of shuffle(pool)) {
            if (out.length >= n) break;
            if (!seenKeys.has(e.k)) {
                seenKeys.add(e.k);
                out.push(e.l);
            }
        }
        return out;
    };
    const selectedVrais = pick(vraiPool, nbVrais);
    const selectedFaux = pick(fauxPool, nbFaux);
    if (selectedVrais.length < nbVrais || selectedFaux.length < nbFaux) {
        return genVF();
    }

    const mixed = shuffle([
        ...selectedVrais.map(l => ({ l, r: 0 })),
        ...selectedFaux.map(l => ({ l, r: 1 })),
    ]);
    const lignes = mixed.map(x => x.l);
    const bonnesReponses = mixed.map(x => x.r);

    const texte = seriesEnonce(ctx, fresh.seriesText)
        + `<p>Indique si chaque affirmation est vraie ou fausse.</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts());
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

// ─── TEXTE À TROUS (DRAG) ──────────────────────────────────

const genTrouDrag = () => {
    const ctx = randomChoice(CONTEXTS);
    const variant = randInt(1, 2);
    let fresh = generateSeries(ctx);
    // Modèle 1 : mode strictement unique par re-tirage (revue 2026-08-08) —
    // sans garde, 43 % des tirages avaient un co-champion, servi comme
    // étiquette et compté faux dans 546 tirages sur 6000.
    if (variant === 1) {
        for (let t = 0; t < 40; t++) {
            const maxC = Math.max(...fresh.counts);
            if (fresh.counts.filter(c => c === maxC).length === 1) break;
            fresh = generateSeries(ctx);
        }
        if (fresh.counts.filter(c => c === Math.max(...fresh.counts)).length > 1) return genTrouDrag();
    }
    const { mods, counts, total } = fresh;
    const mode = getMode(mods, counts);

    let phrase, distracteurs, secours;

    if (variant === 1) {
        // Model 1
        const i1 = randInt(0, mods.length - 1);
        let i2 = (i1 + 1) % mods.length;
        phrase = `${mods[i1]} apparaît {{${counts[i1]}}} fois et ${mods[i2]} apparaît {{${counts[i2]}}} fois. ${artDef(ctx.genre, ctx.label)}${ctx.label} ${ctx.genre === 'm' ? 'le plus fréquent' : 'la plus fréquente'} est {{${mode}}}.`;
        const wrongMod = randomChoice(mods.filter(m => m !== mode));
        distracteurs = [
            String(counts[i1] + 1),
            String(counts[i2] - 1),
            wrongMod,
        ];
        secours = [String(counts[i1] + 2), String(Math.max(1, counts[i2] - 2)), String(total), String(total + 1)];
    } else {
        // Model 2
        phrase = `Le total des observations est de {{${total}}}. Il y a {{${mods.length}}} ${ctx.labelPlural} différentes.`;
        distracteurs = [
            String(total + 1),
            String(total - 1),
            String(mods.length + 1),
        ];
        secours = [String(total + 2), String(mods.length + 2)];
    }

    // Repli (retouche 2026-07-28) : sur le contexte « dés », les modalités
    // sont NUMÉRIQUES — les trois candidats peuvent tous percuter un mot de
    // trou et filtrerDistracteurs vidait la liste (drag pré-résolu,
    // L-TROU-SANS-DISTRACTEURS). On complète avec des voisins plus éloignés
    // jusqu'à garantir au moins 2 étiquettes leurres.
    let tuiles = filtrerDistracteurs(phrase, distracteurs);
    for (const c of secours) {
        if (tuiles.length >= 2) break;
        tuiles = filtrerDistracteurs(phrase, [...tuiles, c]);
    }

    const texte = seriesEnonce(ctx, fresh.seriesText)
        + `<p>Complète les phrases en glissant les étiquettes.</p>`;

    const payload = makeTexteTrou(phrase, texte, {
        ...opts(),
        mode: 'drag',
        distracteurs: tuiles,
    });
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

// ─── TEXTE À TROUS (SAISIE) ────────────────────────────────

const genTrouSaisie = () => {
    const ctx = randomChoice(CONTEXTS);
    const fresh = generateSeries(ctx);
    const { mods, counts, total } = fresh;

    const variant = randInt(1, 2);

    let phrase;

    if (variant === 1) {
        // Model 1
        const idx = randInt(0, mods.length - 1);
        phrase = `${mods[idx]} apparaît {{${counts[idx]}}} fois dans la série.`;
    } else {
        // Model 2
        phrase = `Le total des observations est de {{${total}}}.`;
    }

    const texte = seriesEnonce(ctx, fresh.seriesText)
        + `<p>Complète la phrase.</p>`;

    // Pas de strict (revue 2026-08-08) : on teste un comptage, la comparaison
    // par valeur suffit (5 = 5,0 accepté).
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(),
        mode: 'saisie',
    });
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

// ─── Registry & Export ──────────────────────────────────────

const GENERATORS = {
    construction_tableau_effectifs_qcm_unique: () => randomChoice([genQcmCount, genQcmMode, genQcmTotal])(),
    construction_tableau_effectifs_texte: genSaisie,
    construction_tableau_effectifs_relier: genRelier,
    construction_tableau_effectifs_tableau_vf: genVF,
    construction_tableau_effectifs_texte_trou_drag: genTrouDrag,
    construction_tableau_effectifs_texte_trou_saisie: genTrouSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    texte: 'Saisie',
    relier: 'Relier',
    tableau_vf: 'Vrai/Faux',
    texte_trou_drag: 'Texte à trous (drag)',
    texte_trou_saisie: 'Texte à trous (saisie)',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({
            key: k,
            label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k,
        }));

export const CATEGORIES = [
    {
        id: 'construction_tableau_effectifs',
        label: 'Construire un tableau simple pour présenter des données',
        formatsList: buildFormats('construction_tableau_effectifs'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // La clé du FORMAT résolu, TOUJOURS (pas celle de la carte posée par les
    // sous-générateurs) : c est elle que la carte d exerciseur compare pour
    // ses compteurs et que la réutilisation range (bug des compteurs muets
    // avant tout clic sur un onglet, tranché 2026-09-02).
    payload._meta.generatorKey = key;
    return payload;
};
